import { useDebounceFn } from '@vueuse/core'
import type {
  Concept,
  ConceptDetail,
  ConceptPayload,
  ConceptSubject,
  GetConceptsParams,
  ManageConcept,
  ManageConceptListItem,
  Meta,
  Response,
  Tag,
  TagType
} from '~/models'

// Empty filter values must be omitted: the API rejects empty strings.
const withoutEmptyValues = (params: object) =>
  Object.fromEntries(
    Object.entries(params).map(([key, value]) => [key, value || undefined])
  )

export default function useConcepts() {
  const { t } = useI18n()
  const { fetchAuth, addErrorToast, addSuccessToast, addUndoSaveToast } =
    useHelpers()
  const { originalConcept } = storeToRefs(useUserStore())

  const router = useRouter()

  const isLoading = ref(false)
  const concepts = ref<Concept[]>([])
  const manageConcepts = ref<ManageConceptListItem[]>([])
  const manageConceptsMeta = ref<Meta | null>(null)

  const createConcept = async (
    payload: ManageConcept,
    stayOnPage?: boolean
  ) => {
    const { data, error } = await fetchAuth<Response>('/manage/concepts', {
      fetchOptions: { method: 'POST', body: payload }
    })

    if (error) {
      addErrorToast()
      return
    }

    if (stayOnPage) {
      return data.data
    }
    router.push(`/teacher/concept/${data.data.uuid}`)
  }

  const postExploredConcept = async (uuid: string) => {
    return await fetchAuth(`/concepts/${uuid}/explored/`, {
      fetchOptions: { method: 'POST' }
    })
  }

  const getManageDraftConcept = async (uuid: string) => {
    // 204 (no draft) and errors both resolve without a body.
    const { data } = await fetchAuth<Response<ConceptPayload> | undefined>(
      `/manage/drafts/concepts/${uuid}`
    )

    return data?.data ?? null
  }

  const saveDraftConcept = async ({
    uuid,
    payload
  }: {
    uuid: string
    payload: ConceptPayload
  }) => {
    const payloadWithoutEmptyStrings = Object.fromEntries(
      Object.entries(payload).filter(([_, v]) => v !== '')
    )

    return await fetchAuth(`/manage/drafts/concepts/${uuid}`, {
      fetchOptions: { method: 'POST', body: payloadWithoutEmptyStrings }
    })
  }

  const manageConcept = ref({ related_concepts: [], name: 'test' })
  const getManageConcept = async (uuid: string) => {
    const { data, error } = await fetchAuth<Response>(
      `/manage/concepts/${uuid}`
    )

    if (error) {
      addErrorToast()
      return
    }

    manageConcept.value = data.data
  }

  let latestManageConceptsRequest = 0

  const getManageConcepts = async ({
    page = 1,
    phrase = '',
    perPage = 20,
    sortBy = { key: '', direction: 'asc' },
    filterBy = {
      is_published: undefined,
      has_questions: '',
      has_content: ''
    },
    filterByTracks = [],
    filterByCourses = [],
    filterByQuartiles = [],
    deleted = false
  }: GetConceptsParams = {}) => {
    const tags = [...filterByTracks, ...filterByCourses, ...filterByQuartiles]
    const requestId = ++latestManageConceptsRequest

    const { data, error } = await fetchAuth<Response<ManageConceptListItem[]>>(
      '/manage/concepts',
      {
        fetchOptions: {
          query: {
            page,
            phrase: phrase || undefined,
            per_page: perPage || undefined,
            sort_by: sortBy.key || undefined,
            order: sortBy.key ? sortBy.direction : undefined,
            ...withoutEmptyValues(filterBy),
            tags: tags.length ? tags.join(',') : undefined,
            get_deleted: deleted || undefined
          }
        }
      }
    )

    // A newer request was started meanwhile; don't let this older response overwrite it.
    if (requestId !== latestManageConceptsRequest) {
      return false
    }

    if (error) {
      addErrorToast()
      return true
    }

    manageConcepts.value = data.data
    manageConceptsMeta.value = data.meta
    return true
  }

  const searchManageConcepts = useDebounceFn(
    async (phrase: { query: string }) => {
      if (!phrase.query) return
      isLoading.value = true
      const searchTerm = phrase.query

      if (await getManageConcepts({ phrase: searchTerm })) {
        isLoading.value = false
      }
    },
    500
  )

  const resetManageConcepts = () => {
    setTimeout(() => {
      manageConcepts.value = []
    }, 50)
  }

  const saveManageConcept = async ({
    uuid,
    payload
  }: {
    uuid: string
    payload: ManageConcept
  }) => {
    const { error } = await fetchAuth(`/manage/concepts/${uuid}`, {
      fetchOptions: { method: 'PUT', body: payload }
    })

    if (error) {
      addErrorToast()
      return
    }

    router.push('/teacher')
    addUndoSaveToast(t('p_Concepts.Concept updated successfully'), 'concept')
  }

  async function resetConcept() {
    const original = originalConcept.value
    if (!original) {
      return
    }

    const { error } = await fetchAuth(`/manage/concepts/${original.uuid}`, {
      fetchOptions: { method: 'PUT', body: original.payload }
    })

    if (error) {
      addErrorToast()
      return
    }

    router.push(`/teacher/concept/${original.uuid}`)
    addSuccessToast(t('p_Teacher.Concept reset successfully'))
    originalConcept.value = null
  }

  const publishConcept = async (uuid: string) => {
    return fetchAuth(`/manage/concepts/${uuid}/publish`, {
      fetchOptions: { method: 'PATCH' }
    })
  }

  const unpublishConcept = async (uuid: string) => {
    return fetchAuth(`/manage/concepts/${uuid}/retract`, {
      fetchOptions: { method: 'PATCH' }
    })
  }

  const deleteConcept = async (uuid: string) => {
    return fetchAuth(`/manage/concepts/${uuid}`, {
      fetchOptions: { method: 'DELETE' }
    })
  }

  async function forceDeleteConcept(uuid: string) {
    return fetchAuth(`/manage/concepts/${uuid}/force-delete`, {
      fetchOptions: { method: 'DELETE' }
    })
  }

  async function restoreConcept(uuid: string) {
    return fetchAuth(`/manage/concepts/${uuid}/restore`, {
      fetchOptions: { method: 'PATCH' }
    })
  }

  const concept = ref<ConceptDetail | null>(null)
  const conceptsMeta = ref<Meta | null>(null)
  const subjects = ref<ConceptSubject[]>([])
  const isError = ref(false)

  const conceptSubjects = ['application', 'relevance'] as const

  const getConcepts = async ({
    page = 1,
    phrase = '',
    perPage = 20,
    sortBy = { key: '', direction: 'asc' },
    filterBy = {
      is_published: 'true',
      has_questions: '',
      has_content: ''
    },
    filterByTracks = [],
    filterByCourses = [],
    filterByQuartiles = [],
    filterByStatus = []
  }: GetConceptsParams = {}) => {
    isLoading.value = true

    const tags = [...filterByTracks, ...filterByCourses, ...filterByQuartiles]

    const { data, error } = await fetchAuth<Response<Concept[]>>('/concepts', {
      fetchOptions: {
        query: {
          page,
          phrase: phrase || undefined,
          per_page: perPage || undefined,
          sort_by: sortBy.key || undefined,
          order: sortBy.key ? sortBy.direction : undefined,
          ...withoutEmptyValues(filterBy),
          tags: tags.length ? tags.join(',') : undefined,
          for_user_status: filterByStatus.length
            ? filterByStatus.join(',')
            : undefined
        }
      }
    })

    isLoading.value = false

    if (error) {
      addErrorToast()
      return
    }

    concepts.value = data.data
    conceptsMeta.value = data.meta
  }

  const searchConcepts = useDebounceFn(async (phrase: { query: string }) => {
    if (!phrase.query) return
    isLoading.value = true
    const searchTerm = phrase.query
    await getConcepts({ phrase: searchTerm })
    isLoading.value = false
  }, 500)

  const resetConcepts = () => {
    setTimeout(() => {
      concepts.value = []
    }, 100)
  }

  const getConcept = async (uuid: string) => {
    if (uuid === undefined) {
      router.push('/')
      return
    }

    isLoading.value = true
    const { data, error } = await fetchAuth<Response<ConceptDetail>>(
      `/concepts/${uuid}`
    )
    isLoading.value = false

    if (error) {
      router.push('/')
      return
    }

    concept.value = data.data
    conceptSubjects.forEach((subject) => {
      const { title, content } = data.data[subject]

      if (!title || !content) {
        return
      }

      subjects.value.push({ title, content })
    })
  }

  const getTabs = (tabs: string[]) =>
    tabs.map((tab) => {
      return {
        id: tab.trim().replace(/\s/g, '').toLowerCase(),
        name: tab
      }
    })

  const relatedConcepts = ref([])

  const getRelatedConcepts = async (uuid: string) => {
    isLoading.value = true
    const { data, error } = await fetchAuth<Response>(
      `/concepts/${uuid}/related-concepts`
    )

    if (error) {
      isLoading.value = false
      isError.value = true
      addErrorToast()
      return
    }

    isLoading.value = false
    relatedConcepts.value = data.data

    return data.data
  }

  async function addTagToConcept({
    uuid,
    name,
    code,
    type
  }: {
    uuid: string
    name: string
    code?: string
    type: TagType
  }) {
    const { data, error } = await fetchAuth<Tag>(
      `/manage/concepts/${uuid}/attach-tag`,
      {
        fetchOptions: {
          method: 'POST',
          body: { name: name, code: code, type: type }
        }
      }
    )

    if (error) {
      addErrorToast()
      return
    }

    return data
  }

  function getStatus(status: string) {
    if (status === 'has_attempted') {
      return {
        icon: 'circle-play',
        class: 'text-emerald-500',
        title: t('c_RelatedConcepts.Has attempted')
      }
    }

    if (status === 'has_finished') {
      return {
        icon: 'check-circle',
        class: 'text-emerald-500',
        title: t('c_RelatedConcepts.Has finished')
      }
    }
    if (status === 'has_completed') {
      return {
        icon: 'circle-star',
        class: 'text-yellow-500',
        title: t('c_RelatedConcepts.Has completed')
      }
    }

    return {
      icon: 'question-circle',
      class: 'text-zinc-300',
      title: t('c_RelatedConcepts.Not started yet')
    }
  }

  return {
    createConcept,
    saveDraftConcept,
    getManageDraftConcept,
    manageConcept,
    postExploredConcept,
    getManageConcept,
    getManageConcepts,
    searchManageConcepts,
    resetManageConcepts,
    manageConcepts,
    manageConceptsMeta,
    saveManageConcept,
    publishConcept,
    unpublishConcept,
    deleteConcept,
    forceDeleteConcept,
    restoreConcept,
    resetConcept,
    getConcepts,
    searchConcepts,
    resetConcepts,
    getConcept,
    concept,
    subjects,
    isLoading,
    isError,
    getTabs,
    relatedConcepts,
    getRelatedConcepts,
    conceptSubjects,
    addTagToConcept,
    getStatus,
    concepts,
    conceptsMeta
  }
}
